import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Modal } from "react-bootstrap";
import ReactMarkdown from "react-markdown";

export interface SentenciaInterpretar {
    region: string;
    organoJurisdiccional: string;
    distrito: string;
    materia: string;
    numero: string;
    anio: string;
    urlSentencia: string;
}

interface Props {
    show: boolean;
    onHide: () => void;
    sentencia: SentenciaInterpretar | null;
}

type Step = "terminos" | "procesando" | "exito" | "resultado" | "error";

const RESTFUL_URL = "https://traductorsentenciasbk.pjedomex.gob.mx/api/interpretar_sp";

const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1070,
};

const ProcesandoOverlay = () => (
    <div style={overlayStyle}>
        <div
            style={{
                background: "#fff",
                borderRadius: 8,
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                padding: "48px 40px",
                textAlign: "center",
                minWidth: 300,
                maxWidth: 400,
                width: "90%",
            }}
        >
            <h5 style={{ color: "#D10A35", fontWeight: 700, marginBottom: 12 }}>
                Procesando Sentencia
            </h5>
            <p style={{ color: "#6c757d", marginBottom: 24 }}>Espere por favor.</p>
            <div
                className="spinner-border"
                role="status"
                style={{ color: "#D10A35", width: "2.5rem", height: "2.5rem" }}
            >
                <span className="visually-hidden">Cargando...</span>
            </div>
        </div>
    </div>
);

const ExitoOverlay = ({ onClose }: { onClose: () => void }) => (
    <div style={overlayStyle}>
        <div
            style={{
                background: "#fff",
                borderRadius: 8,
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                padding: "48px 40px",
                textAlign: "center",
                minWidth: 300,
                maxWidth: 400,
                width: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 72,
                height: 72,
                borderRadius: "50%",
                border: "3px solid #a5d6a7",
                marginBottom: 16,
            }}>
                <span style={{ fontSize: "2rem", color: "#2e7d32" }}>✓</span>
            </div>
            <h5 style={{ color: "#212529", fontWeight: 700, marginBottom: 8 }}>
                Interpretación completada.
            </h5>
            <p style={{ color: "#6c757d", fontSize: "0.88rem", marginBottom: 28 }}>
                El contenido mostrado es generado automáticamente mediante inteligencia artificial, con el propósito de facilitar la comprensión general y no tiene ningún valor legal.
            </p>
            <button
                onClick={onClose}
                style={{
                    backgroundColor: "#3085d6",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    fontSize: 15,
                    fontWeight: 500,
                    padding: "10px 36px",
                    cursor: "pointer",
                }}
            >
                OK
            </button>
        </div>
    </div>
);

export const ModalInterpretarSentencia = ({ show, onHide, sentencia }: Props) => {
    const [step, setStep] = useState<Step>("terminos");
    const [resumen, setResumen] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    const handleEnviarSentencia = useCallback(async () => {
        setStep("procesando");

        try {
            const response = await fetch(RESTFUL_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    region: String(sentencia?.region ?? ""),
                    organoJurisdiccional: String(sentencia?.organoJurisdiccional ?? ""),
                    distrito: String(sentencia?.distrito ?? ""),
                    materia: String(sentencia?.materia ?? ""),
                    numero: String(sentencia?.numero ?? ""),
                    anio: String(sentencia?.anio ?? ""),
                    urlSentencia: String(sentencia?.urlSentencia ?? ""),
                }),
            });

            const data = await response.json();

            if (data.success && data.data?.summary) {
                setResumen(data.data.summary);
                setStep("exito");
            } else {
                setErrorMsg(
                    data.error?.message ||
                    data.message ||
                    "El servicio no pudo procesar el documento."
                );
                setStep("error");
            }
        } catch {
            setErrorMsg(
                "No fue posible conectar con el servicio de interpretación. Intente más tarde."
            );
            setStep("error");
        }
    }, [sentencia]);

    useEffect(() => {
        if (show && step === "terminos" && sentencia) {
            handleEnviarSentencia();
        }
    }, [show, step, sentencia, handleEnviarSentencia]);

    const handleClose = () => {
        setStep("terminos");
        setResumen("");
        setErrorMsg("");
        onHide();
    };

    if (step === "procesando") {
        return createPortal(<ProcesandoOverlay />, document.body);
    }

    if (step === "exito") {
        return createPortal(
            <ExitoOverlay onClose={() => setStep("resultado")} />,
            document.body
        );
    }

    return (
        <>
            {show && createPortal(
                <div
                    onClick={handleClose}
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 1045,
                    }}
                />,
                document.body
            )}

            <Modal
                show={show}
                onHide={handleClose}
                size="xl"
                dialogClassName="modal-90w"
                centered
                scrollable
                backdrop={false}
            >
                {step === "resultado" && (
                    <>
                        <Modal.Header closeButton className="border-bottom-0" style={{ background: "#fff" }}>
                            <Modal.Title
                                className="w-100 text-center fw-bold"
                                style={{ color: "#D10A35", fontSize: "1.1rem" }}
                            >
                                Sentencia Ciudadana con IA
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body className="px-4 w-600" style={{ background: "#fff" }}>
                            <h6 style={{ color: "#D10A35", fontWeight: 700, marginBottom: 8 }}>
                                Resumen ciudadano
                            </h6>
                            <div style={{
                                background: "#fff",
                                borderRadius: 4,
                                padding: 12,
                                fontSize: "0.92rem",
                                lineHeight: 1.7,
                                whiteSpace: "pre-wrap",
                                maxHeight: "60vh",
                                overflowY: "auto",
                                color: "#212529",
                            }}>
                                <ReactMarkdown>{resumen}</ReactMarkdown>
                            </div>
                        </Modal.Body>

                        <Modal.Footer className="border-top-0 justify-content-center pb-2" style={{ background: "#fff" }}>
                        </Modal.Footer>
                    </>
                )}

                {step === "error" && (
                    <>
                        <Modal.Header closeButton className="border-bottom-0" style={{ background: "#fff" }}>
                            <Modal.Title
                                className="w-100 text-center fw-bold"
                                style={{ color: "#D10A35", fontSize: "1.1rem" }}
                            >
                                Error al procesar la sentencia
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body className="text-center py-4 px-4" style={{ background: "#fff" }}>
                            <p style={{ color: "#c62828", fontWeight: 600, marginBottom: 8 }}>
                                No fue posible procesar la sentencia.
                            </p>
                            <p style={{ color: "#6c757d", fontSize: "0.88rem", maxWidth: 380, margin: "0 auto" }}>
                                {errorMsg}
                            </p>
                        </Modal.Body>

                        <Modal.Footer className="border-top-0 justify-content-center pb-4" style={{ background: "#fff" }}>
                            <button className="btn btn-secondary me-2" onClick={handleClose}>
                                Cerrar
                            </button>
                            <button
                                className="btn text-white"
                                style={{ background: "#D10A35" }}
                                onClick={() => {
                                    setStep("terminos");
                                    setErrorMsg("");
                                }}
                            >
                                Reintentar
                            </button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </>
    );
};